import * as Icons from '@ant-design/icons';
import { useInvalidate, useNavigation } from '@refinedev/core';
import { Form, Input, Typography } from 'antd';
import { CustomModal } from 'components/modal/CustomModal';
import useNotification from 'hooks/useNotification';
import { useCallback, useEffect, useState } from 'react';
import { useCertificateStore } from '../../certificate.store';

export const ModalNewCertificate = () => {
  const {
    certificate,
    modals: { newCertificate },
    deleteAndReissueCertificate,
    isIssuingCertificate,
    isDeletingCertificate,
    closeNewCertificateModal
  } = useCertificateStore();

  const [form] = Form.useForm();
  const { showSuccess, showError } = useNotification();
  const invalidate = useInvalidate();
  const { push } = useNavigation();

  const handleReissueCertificate = useCallback(async () => {
    if (!certificate?.id) {
      showError('Certificado não encontrado.');
      return;
    }

    try {
      const { observations } = await form.validateFields();

      await deleteAndReissueCertificate(certificate.id, observations);

      // Invalidar queries e fazer reload da rota
      await invalidate({
        resource: 'graduate/courses/enrollments',
        invalidates: ['detail']
      });

      showSuccess(
        'Certificado invalidado com sucesso. Um novo certificado pode ser emitido.'
      );
      closeNewCertificateModal();
      form.resetFields();

      // Reload da rota atual para garantir que os dados sejam atualizados
      push(`${window.location.pathname}?tab=user_info`);
    } catch (error) {
      console.error('Error reissuing certificate:', error);
      showError('Erro ao invalidar certificado. Tente novamente.');
    }
  }, [
    certificate,
    deleteAndReissueCertificate,
    form,
    showSuccess,
    showError,
    closeNewCertificateModal,
    invalidate,
    push
  ]);

  // Reset form when modal closes
  useEffect(() => {
    if (!newCertificate) {
      form.resetFields();
      closeNewCertificateModal();
    }
  }, [newCertificate, form, closeNewCertificateModal]);

  return (
    <Form form={form} onFinish={handleReissueCertificate}>
      <CustomModal
        onCancel={closeNewCertificateModal}
        onOk={form.submit}
        okButtonProps={{
          loading: isDeletingCertificate,
          disabled: isDeletingCertificate
        }}
        icon={<Icons.AuditOutlined />}
        title="Emitir novo certificado?"
        open={newCertificate}
        width={700}
        okText="Emitir novo certificado"
        cancelText="Cancelar"
      >
        <Typography.Paragraph>
          Ao prosseguir, este certificado será <b>invalidado</b> e a matrícula
          retornará para a listagem de <b>certificados a emitir</b> com o status{' '}
          <b>Apto para emissão</b>, precisando passar novamente pelas aprovações
          necessárias.
        </Typography.Paragraph>

        <Typography.Paragraph>
          Após isso, um <b>novo certificado</b> poderá ser emitido com{' '}
          <b>um número de registro diferente</b>, substituindo o anterior. Esta
          ação será registrada no histórico de emissões.
        </Typography.Paragraph>

        <Form.Item
          label="Informe o motivo da nova emissão (obrigatório)"
          rules={[
            { required: true, message: 'Campo obrigatório' },
            { min: 10, message: 'O motivo deve ter pelo menos 10 caracteres' }
          ]}
          name="observations"
          layout="vertical"
        >
          <Input.TextArea
            rows={3}
            disabled={isIssuingCertificate}
            placeholder="Ex: Aluno entrou em contato e solicitou uma nova emissão pois havia perdido o certificado anterior"
            maxLength={100}
            showCount
          />
        </Form.Item>
      </CustomModal>
    </Form>
  );
};
